//deps
import React, { memo, useCallback, useEffect } from "react";

//components
import { AddressNudgeTooltipContainer, AddressNudgeTooltipText, ClearButton } from "./styled";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as WarningFilledIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/warningFilled/16";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

//hooks
import { useCartAddressNudgeAnalytics } from "@cart-client/hooks/useCartAddressNudgeAnalytics";

//type and constants
import { CLEAR_IMAGE } from "./constants";
import { ADDRESS_NUDGE_TOOLTIP_STATUS } from "@swiggy-private/cart-types";
import { ADDRESS_NUDGE_TOOLTIP_TEST_IDS } from "@swiggy-private/cart-test-ids";

interface AddressNudgeToolTipProps {
    isUserBlackMember?: boolean;
    addressNudgeToolTipStatus: ADDRESS_NUDGE_TOOLTIP_STATUS;
    addressNudgeToolTipText?: string;
    onCloseClick: () => void;
}

const AddressNudgeToolTip = ({
    isUserBlackMember = false,
    addressNudgeToolTipStatus,
    addressNudgeToolTipText,
    onCloseClick,
}: AddressNudgeToolTipProps) => {
    const collapseToolTip = addressNudgeToolTipStatus === ADDRESS_NUDGE_TOOLTIP_STATUS.SHOWN;

    const { onImpressionAddressNudgeTooltip, onClickAddressNudgeToolTipClose } = useCartAddressNudgeAnalytics();
    const handleCloseClick = useCallback(() => {
        onClickAddressNudgeToolTipClose();
        onCloseClick();
    }, [onClickAddressNudgeToolTipClose, onCloseClick]);

    useEffect(() => {
        if (addressNudgeToolTipStatus === ADDRESS_NUDGE_TOOLTIP_STATUS.SHOW) {
            onImpressionAddressNudgeTooltip();
        }
    }, [addressNudgeToolTipStatus, onImpressionAddressNudgeTooltip]);

    if (!addressNudgeToolTipText || addressNudgeToolTipStatus === ADDRESS_NUDGE_TOOLTIP_STATUS.NOT_SHOWN) {
        return null;
    }

    return (
        <AddressNudgeTooltipContainer
            data-testid={ADDRESS_NUDGE_TOOLTIP_TEST_IDS.CONTAINER}
            collapse={collapseToolTip}
            themeChange={isUserBlackMember}
        >
            {!collapseToolTip && (
                <>
                    <GenericIcon
                        data-testid={ADDRESS_NUDGE_TOOLTIP_TEST_IDS.ICON}
                        Icon={WarningFilledIcon}
                        fillColor={isUserBlackMember ? "warning" : "status_Noteworthy"}
                    ></GenericIcon>
                    <AddressNudgeTooltipText
                        data-testid={ADDRESS_NUDGE_TOOLTIP_TEST_IDS.TEXT}
                        variant="V2.body3.neutral.medium"
                        color={isUserBlackMember ? "warning" : "text_inverse_Highest_Emphasis"}
                    >
                        {addressNudgeToolTipText}
                    </AddressNudgeTooltipText>
                    <ClearButton
                        data-testid={ADDRESS_NUDGE_TOOLTIP_TEST_IDS.CLOSE_BUTTON}
                        role="button"
                        onClick={handleCloseClick}
                    >
                        <Image
                            cloudinaryId={
                                isUserBlackMember ? CLEAR_IMAGE.ID.FILLED_SUBTLE : CLEAR_IMAGE.ID.FILLED_BLACK
                            }
                            height={CLEAR_IMAGE.HEIGHT}
                            width={CLEAR_IMAGE.WIDTH}
                            fit="contain"
                        />
                    </ClearButton>
                </>
            )}
        </AddressNudgeTooltipContainer>
    );
};

export default memo(AddressNudgeToolTip);
